import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from './lib/axiosClient'; // v2

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://ivision-crm-backend-production.up.railway.app';

const STATUS_COLORS = {
  pending:       { bg: '#2a2a1a', color: '#f0c040', label: 'Pending' },
  in_progress:   { bg: '#1a2a3a', color: '#4ab0f0', label: 'In Progress' },
  waiting_parts: { bg: '#2a1a2a', color: '#c080f0', label: 'Waiting Parts' },
  completed:     { bg: '#1a2a1a', color: '#4caf50', label: 'Completed' },
  cancelled:     { bg: '#2a1a1a', color: '#888',    label: 'Cancelled' },
};

const PRIORITY_COLORS = { low:'#555', normal:'#888', high:'#f0a040', urgent:'#e57373' };

export default function RepairModule() {
  const [repairs, setRepairs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [fleetItems, setFleetItems] = useState([]);
  const [clients, setClients] = useState([]);

  const loadRepairs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('repair_type', filterType);
      if (search) params.set('search', search);
      const [repRes, metRes] = await Promise.all([
        axiosClient.get('/api/rentals/repairs?' + params),
        axiosClient.get('/api/rentals/repairs/metrics'),
      ]);
      const repJson = repRes.data;
      const metJson = metRes.data;
      setRepairs(repJson.repairs || []);
      setMetrics(metJson.metrics || null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus, filterType, search]);

  useEffect(() => { loadRepairs(); }, [loadRepairs]);

  useEffect(() => {
    Promise.all([
      axiosClient.get('/api/rentals/products').then(r => r.data),
      axiosClient.get('/api/rentals/clients').then(r => r.data),
    ]).then(([iJson, cJson]) => {
      // Flatten variants into a list for the repair form
      const prods = iJson.products || [];
      const variants = [];
      prods.forEach(p => {
        (p.rental_product_variants || p.variants || []).forEach(v => {
          variants.push({
            id: v.id,
            name: p.name,
            serial_number: v.serial_number || v.sku || '',
            condition: v.condition,
            hour_meter: v.hour_meter || 0,
            unit_status: v.unit_status,
            product_id: p.id,
          });
        });
      });
      setFleetItems(variants);
      setClients(cJson.clients || cJson || []);
    });
  }, []);

  const openDetail = async (id) => {
    const res = await axiosClient.get('/api/rentals/repairs/' + id);
    const json = res.data;
    setSelected(json);
    setView('detail');
  };

  const handleStatusChange = async (id, newStatus) => {
    await axiosClient.patch('/api/rentals/repairs/' + id, { status: newStatus });
    loadRepairs();
    if (selected?.repair?.id === id) openDetail(id);
  };

  if (view === 'new') return <NewRepairForm fleetItems={fleetItems} clients={clients} onSaved={() => { setView('list'); loadRepairs(); }} onCancel={() => setView('list')} />;
  if (view === 'detail') return <RepairDetail data={selected} onBack={() => { setView('list'); loadRepairs(); }} onStatusChange={handleStatusChange} onRefresh={() => openDetail(selected.repair.id)} />;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div><h2 style={s.title}>🔧 Repairs</h2><p style={s.subtitle}>Work orders for fleet maintenance and customer repairs</p></div>
        <button style={s.btnPrimary} onClick={() => setView('new')}>+ New Work Order</button>
      </div>
      {metrics && (
        <div style={s.metricsRow}>
          {[
            { label: 'Open', value: metrics.pending + metrics.in_progress + metrics.waiting_parts, color: '#f0c040' },
            { label: 'In Progress', value: metrics.in_progress, color: '#4ab0f0' },
            { label: 'Waiting Parts', value: metrics.waiting_parts, color: '#c080f0' },
            { label: 'Completed', value: metrics.completed, color: '#4caf50' },
            { label: 'Fleet Jobs', value: metrics.internal, color: '#888' },
            { label: 'Customer Jobs', value: metrics.customer, color: '#888' },
            { label: 'Revenue', value: '$' + (metrics.revenue_total || 0).toLocaleString('en', { minimumFractionDigits: 0 }), color: '#4caf50' },
            { label: 'Outstanding', value: '$' + (metrics.revenue_outstanding || 0).toLocaleString('en', { minimumFractionDigits: 0 }), color: '#f0c040' },
          ].map(m => (
            <div key={m.label} style={s.metricCard}>
              <div style={{ ...s.metricValue, color: m.color }}>{m.value}</div>
              <div style={s.metricLabel}>{m.label}</div>
            </div>
          ))}
        </div>
      )}
      <div style={s.filtersRow}>
        <input style={s.searchInput} placeholder="Search WO #, make, model, serial..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_COLORS).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
        </select>
        <select style={s.select} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          <option value="internal">Fleet (Internal)</option>
          <option value="customer">Customer</option>
        </select>
      </div>
      {loading ? <div style={s.empty}>Loading repairs...</div> : repairs.length === 0 ? (
        <div style={s.empty}>No work orders found. <span style={{ color: '#4caf50', cursor: 'pointer' }} onClick={() => setView('new')}>Create one →</span></div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.tableHeader}>
            <span>WO #</span><span>Type</span><span>Machine</span><span>Client / Item</span><span>Status</span><span>Priority</span><span>Received</span><span>Total</span>
          </div>
          {repairs.map(r => (
            <div key={r.id} style={s.tableRow} onClick={() => openDetail(r.id)}>
              <span style={s.woNum}>{r.wo_number}</span>
              <span><span style={{ ...s.typeBadge, background: r.repair_type === 'internal' ? '#1a2a1a' : '#1a1a2a', color: r.repair_type === 'internal' ? '#4caf50' : '#4ab0f0' }}>{r.repair_type === 'internal' ? '🔩 Fleet' : '👤 Customer'}</span></span>
              <span><div style={{ fontWeight: 600, color: '#ddd' }}>{r.machine_make} {r.machine_model}</div>{r.serial_number && <div style={{ fontSize: 11, color: '#666' }}>S/N: {r.serial_number}</div>}</span>
              <span style={{ fontSize: 12, color: '#aaa' }}>{r.repair_type === 'internal' ? r.rental_items?.name || '—' : r.rental_clients?.name || '—'}</span>
              <span><span style={{ ...s.statusBadge, background: STATUS_COLORS[r.status]?.bg, color: STATUS_COLORS[r.status]?.color }}>{STATUS_COLORS[r.status]?.label}</span></span>
              <span style={{ color: PRIORITY_COLORS[r.priority], fontWeight: 600, fontSize: 12 }}>{r.priority?.toUpperCase()}</span>
              <span style={{ fontSize: 12, color: '#888' }}>{r.received_date ? new Date(r.received_date).toLocaleDateString() : '—'}</span>
              <span style={{ fontWeight: 600 }}>{r.repair_type === 'customer' && r.total_amount > 0 ? '$' + parseFloat(r.total_amount).toFixed(2) : <span style={{ color: '#555' }}>—</span>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewRepairForm({ fleetItems, clients, onSaved, onCancel }) {
  const [form, setForm] = useState({
    repair_type: 'internal',
    variant_id: '', rental_item_id: '', client_id: '',
    machine_model: '', serial_number: '', hour_meter: '',
    description: '', priority: 'normal',
    assigned_to: '', vendor_name: '',
    received_date: new Date().toISOString().split('T')[0],
    promised_date: '', internal_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.description.trim()) return setError('Problem description is required.');
    setSaving(true); setError(null);
    try {
      const res = await axiosClient.post('/api/rentals/repairs', {
        ...form,
        variant_id: form.variant_id || null,
        rental_item_id: form.rental_item_id || null,
        client_id: form.client_id || null,
        hour_meter: form.hour_meter !== '' ? Number(form.hour_meter) : null,
        labor_rate: form.labor_rate !== '' ? Number(form.labor_rate) : null,
      });
      onSaved(res.data.repair);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setSaving(false); }
  };

  const selectedUnit = fleetItems.find(x => x.id === form.rental_item_id);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>New Work Order</h2>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Log a repair for fleet or customer equipment</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={s.btnSecondary} onClick={onCancel}>Cancel</button>
          <button style={s.btnPrimary} onClick={handleSubmit} disabled={saving}>{saving ? 'Creating...' : 'Create Work Order'}</button>
        </div>
      </div>

      {error && <div style={{ background: '#2a0a0a', border: '1px solid #a03030', color: '#ff8080', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>{error}</div>}

      <div style={s.formBox}>
        {/* Type toggle */}
        <div style={{ marginBottom: 20 }}>
          <div style={s.sectionLabel}>What needs repair?</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            {[['internal', '🔩 Our Equipment'], ['customer', '👤 Customer Machine']].map(([v, l]) => (
              <button key={v} style={{ ...s.typeBtn, ...(form.repair_type === v ? s.typeBtnActive : {}) }} onClick={() => set('repair_type', v)}>{l}</button>
            ))}
          </div>
        </div>

        {/* Equipment selector */}
        {form.repair_type === 'internal' ? (
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>Select Machine *</label>
            <select style={s.input} value={form.rental_item_id} onChange={e => {
              set('rental_item_id', e.target.value);
              set('variant_id', e.target.value);
              const v = fleetItems.find(x => x.id === e.target.value);
              if (v) { set('machine_model', v.name); set('serial_number', v.serial_number || ''); set('hour_meter', v.hour_meter || ''); }
            }}>
              <option value="">-- Select equipment --</option>
              {fleetItems.map(i => <option key={i.id} value={i.id}>{i.name}{i.serial_number ? ' · ' + i.serial_number : ''}</option>)}
            </select>
            {selectedUnit && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#1a1c2e', border: '1px solid #252840', borderRadius: 6, fontSize: 12, color: '#888', display: 'flex', gap: 16 }}>
                <span>S/N: <span style={{ color: '#fff' }}>{selectedUnit.serial_number || '—'}</span></span>
                <span>Condition: <span style={{ color: '#fff', textTransform: 'capitalize' }}>{selectedUnit.condition || '—'}</span></span>
                <span>Hours: <span style={{ color: '#fff' }}>{selectedUnit.hour_meter || 0}</span></span>
                <span>Status: <span style={{ color: selectedUnit.unit_status === 'available' ? '#4ade80' : '#f87171', textTransform: 'capitalize' }}>{selectedUnit.unit_status}</span></span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>Customer *</label>
            <select style={s.input} value={form.client_id} onChange={e => set('client_id', e.target.value)}>
              <option value="">-- Select client --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Machine / Model</label>
                <input style={s.input} value={form.machine_model} onChange={e => set('machine_model', e.target.value)} placeholder="e.g. Husqvarna PG 820" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Serial Number</label>
                <input style={s.input} value={form.serial_number} onChange={e => set('serial_number', e.target.value)} placeholder="S/N" />
              </div>
            </div>
          </div>
        )}

        {/* Problem */}
        <div style={{ marginBottom: 16 }}>
          <label style={s.label}>What's wrong? *</label>
          <textarea style={{ ...s.input, minHeight: 90, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the problem clearly..." />
        </div>

        {/* Who's fixing it + when */}
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={s.label}>Priority</label>
            <select style={s.input} value={form.priority} onChange={e => set('priority', e.target.value)}>
              {[['low','Low'],['normal','Normal'],['high','High'],['urgent','🚨 Urgent']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Assigned To (Internal)</label>
            <input style={s.input} value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)} placeholder="Tech name" />
          </div>
          <div>
            <label style={s.label}>External Shop</label>
            <input style={s.input} value={form.vendor_name || ''} onChange={e => set('vendor_name', e.target.value)} placeholder="Shop name" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={s.label}>Date Received</label>
            <input style={s.input} type="date" value={form.received_date} onChange={e => set('received_date', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Promised Return Date</label>
            <input style={s.input} type="date" value={form.promised_date} onChange={e => set('promised_date', e.target.value)} />
          </div>
        </div>

        <div>
          <label style={s.label}>Notes</label>
          <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={form.internal_notes} onChange={e => set('internal_notes', e.target.value)} placeholder="Any additional notes..." />
        </div>
      </div>
    </div>
  );
}

function RepairDetail({ data, onBack, onStatusChange, onRefresh }) {
  const { repair, lineItems = [], history = [] } = data || {};
  const [activeTab, setActiveTab] = useState('details');
  const [editingField, setEditing] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [newLine, setNewLine] = useState({ line_type: 'part', description: '', quantity: 1, unit_price: 0, part_number: '' });
  const [saving, setSaving] = useState(false);
  if (!repair) return <div style={s.empty}>Loading...</div>;
  const sc = STATUS_COLORS[repair.status] || STATUS_COLORS.pending;

  const saveField = async (field, value) => {
    setSaving(true);
    await axiosClient.patch('/api/rentals/repairs/' + repair.id, { [field]: value });
    setSaving(false); setEditing(null); onRefresh();
  };

  const addLine = async () => {
    if (!newLine.description) return;
    setSaving(true);
    await axiosClient.get('/api/rentals/repairs/' + repair.id + '/lines');
    setSaving(false);
    setNewLine({ line_type: 'part', description: '', quantity: 1, unit_price: 0, part_number: '' });
    onRefresh();
  };

  const deleteLine = async (lineId) => {
    await axiosClient.get('/api/rentals/repairs/' + repair.id + '/lines/' + lineId);
    onRefresh();
  };

  const laborTotal = parseFloat(repair.labor_rate || 0) * parseFloat(repair.labor_hours || 0);
  const partsTotal = parseFloat(repair.parts_total || 0);
  const tax = parseFloat(repair.tax_percent || 0);
  const subtotal = laborTotal + partsTotal;
  const total = subtotal + (subtotal * tax / 100);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button style={s.backBtn} onClick={onBack}>← Back</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ ...s.title, marginBottom: 0 }}>{repair.wo_number}</h2>
              <span style={{ ...s.statusBadge, background: sc.bg, color: sc.color }}>{sc.label}</span>
            </div>
            <p style={s.subtitle}>{repair.machine_make} {repair.machine_model}{repair.serial_number ? ' · S/N ' + repair.serial_number : ''}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_COLORS).filter(([v]) => v !== repair.status && v !== 'cancelled').map(([v, c]) => (
            <button key={v} style={{ border: '1px solid ' + c.color, background: c.bg, color: c.color, borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }} onClick={() => onStatusChange(repair.id, v)}>→ {c.label}</button>
          ))}
        </div>
      </div>
      <div style={s.tabs}>
        {['details', 'parts & labor', 'history'].map(t => (
          <button key={t} style={{ ...s.tab, ...(activeTab === t ? s.tabActive : {}) }} onClick={() => setActiveTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>
      {activeTab === 'details' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={s.card}>
            <div style={s.cardTitle}>Machine</div>
            {[['Make', 'machine_make'], ['Model', 'machine_model'], ['Serial No.', 'serial_number'], ['Hour Meter', 'hour_meter'], ['Fuel Type', 'fuel_type']].map(([label, field]) => (
              <div key={field} style={s.fieldRow}>
                <span style={s.fieldLabel}>{label}</span>
                {editingField === field ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input style={s.inlineInput} value={fieldValue} onChange={e => setFieldValue(e.target.value)} autoFocus />
                    <button style={s.btnSave} onClick={() => saveField(field, fieldValue)} disabled={saving}>✓</button>
                    <button style={s.btnCancel} onClick={() => setEditing(null)}>✕</button>
                  </div>
                ) : (
                  <span style={s.fieldValue} onClick={() => { setEditing(field); setFieldValue(repair[field] || ''); }}>
                    {repair[field] || <span style={{ color: '#555' }}>—</span>}<span style={{ color: '#444', fontSize: 10 }}> ✎</span>
                  </span>
                )}
              </div>
            ))}
          </div>
          <div style={s.card}>
            <div style={s.cardTitle}>{repair.repair_type === 'customer' ? 'Customer' : 'Fleet Item'}</div>
            <div style={s.fieldRow}>
              <span style={s.fieldLabel}>{repair.repair_type === 'customer' ? 'Client' : 'Equipment'}</span>
              <span style={s.fieldValue}>{repair.repair_type === 'customer' ? repair.rental_clients?.name || '—' : repair.rental_items?.name || '—'}</span>
            </div>
            {[['Received', 'received_date'], ['Promised', 'promised_date'], ['Assigned', 'assigned_to']].map(([label, field]) => (
              <div key={field} style={s.fieldRow}>
                <span style={s.fieldLabel}>{label}</span>
                {editingField === field ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input style={s.inlineInput} type={field.includes('date') ? 'date' : 'text'} value={fieldValue} onChange={e => setFieldValue(e.target.value)} autoFocus />
                    <button style={s.btnSave} onClick={() => saveField(field, fieldValue)} disabled={saving}>✓</button>
                    <button style={s.btnCancel} onClick={() => setEditing(null)}>✕</button>
                  </div>
                ) : (
                  <span style={s.fieldValue} onClick={() => { setEditing(field); setFieldValue(repair[field] || ''); }}>
                    {repair[field] ? (field.includes('date') ? new Date(repair[field]).toLocaleDateString() : repair[field]) : <span style={{ color: '#555' }}>—</span>}<span style={{ color: '#444', fontSize: 10 }}> ✎</span>
                  </span>
                )}
              </div>
            ))}
          </div>
          <div style={{ ...s.card, gridColumn: '1 / -1' }}>
            <div style={s.cardTitle}>Job Notes</div>
            {[['Problem Description', 'description'], ['Diagnosis', 'diagnosis'], ['Resolution', 'resolution'], ['Internal Notes', 'internal_notes']].map(([label, field]) => (
              <div key={field} style={{ marginBottom: 14 }}>
                <div style={s.fieldLabel}>{label}</div>
                {editingField === field ? (
                  <div>
                    <textarea style={{ ...s.inlineInput, width: '100%', minHeight: 70, resize: 'vertical', marginBottom: 6 }} value={fieldValue} onChange={e => setFieldValue(e.target.value)} autoFocus />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={s.btnSave} onClick={() => saveField(field, fieldValue)} disabled={saving}>Save</button>
                      <button style={s.btnCancel} onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ ...s.fieldValue, cursor: 'pointer', whiteSpace: 'pre-wrap' }} onClick={() => { setEditing(field); setFieldValue(repair[field] || ''); }}>
                    {repair[field] || <span style={{ color: '#555' }}>Click to add...</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'parts & labor' && (
        <div style={s.card}>
          <div style={s.cardTitle}>Line Items</div>
          {lineItems.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
              <thead><tr style={{ background: '#1a1a1a' }}>
                {['Type', 'Description', 'Part #', 'Qty', 'Unit $', 'Subtotal', ''].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#555', textTransform: 'uppercase' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {lineItems.map(l => (
                  <tr key={l.id} style={{ borderTop: '1px solid #1a1a1a' }}>
                    <td style={{ padding: '9px 10px' }}><span style={{ background: '#222', color: '#888', borderRadius: 3, padding: '2px 7px', fontSize: 11 }}>{l.line_type}</span></td>
                    <td style={{ padding: '9px 10px', color: '#ccc' }}>{l.description}</td>
                    <td style={{ padding: '9px 10px', color: '#666' }}>{l.part_number || '—'}</td>
                    <td style={{ padding: '9px 10px', color: '#ccc' }}>{l.quantity}</td>
                    <td style={{ padding: '9px 10px', color: '#ccc' }}>${parseFloat(l.unit_price).toFixed(2)}</td>
                    <td style={{ padding: '9px 10px', color: '#ccc' }}>${parseFloat(l.subtotal).toFixed(2)}</td>
                    <td style={{ padding: '9px 10px' }}><button style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 14 }} onClick={() => deleteLine(l.id)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            <select style={{ ...s.input, flex: '0 0 100px' }} value={newLine.line_type} onChange={e => setNewLine(p => ({ ...p, line_type: e.target.value }))}>
              {['part', 'labor', 'sublet', 'misc'].map(t => <option key={t}>{t}</option>)}
            </select>
            <input style={{ ...s.input, flex: 2 }} placeholder="Description" value={newLine.description} onChange={e => setNewLine(p => ({ ...p, description: e.target.value }))} />
            <input style={{ ...s.input, width: 100 }} placeholder="Part #" value={newLine.part_number} onChange={e => setNewLine(p => ({ ...p, part_number: e.target.value }))} />
            <input style={{ ...s.input, width: 60 }} type="number" placeholder="Qty" value={newLine.quantity} onChange={e => setNewLine(p => ({ ...p, quantity: e.target.value }))} />
            <input style={{ ...s.input, width: 80 }} type="number" placeholder="$ each" value={newLine.unit_price} onChange={e => setNewLine(p => ({ ...p, unit_price: e.target.value }))} />
            <button style={s.btnPrimary} onClick={addLine} disabled={saving || !newLine.description}>Add</button>
          </div>
          <div style={{ background: '#0a0a0a', borderRadius: 6, padding: '12px 16px', maxWidth: 360, marginLeft: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: '#aaa', borderBottom: '1px solid #1a1a1a' }}><span>Labor ({repair.labor_hours || 0} hrs × ${repair.labor_rate || 0}/hr)</span><span>${laborTotal.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: '#aaa', borderBottom: '1px solid #1a1a1a' }}><span>Parts</span><span>${partsTotal.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 15, fontWeight: 700, color: '#fff' }}><span>TOTAL</span><span>${total.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: '#4caf50' }}><span>Paid</span><span>${parseFloat(repair.amount_paid || 0).toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: '#f0c040' }}><span>Balance Due</span><span>${Math.max(0, total - parseFloat(repair.amount_paid || 0)).toFixed(2)}</span></div>
          </div>
        </div>
      )}
      {activeTab === 'history' && (
        <div style={s.card}>
          <div style={s.cardTitle}>Service History</div>
          {history.length === 0 ? <div style={s.empty}>No history yet.</div> : (
            <div style={{ paddingLeft: 20 }}>
              {history.map(h => (
                <div key={h.id} style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#4caf50', marginTop: 4, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{new Date(h.event_date).toLocaleDateString()}{h.hour_meter ? ' · ' + h.hour_meter + ' hrs' : ''}</div>
                    <div style={{ fontSize: 13, color: '#ddd' }}>{h.summary}</div>
                    {h.performed_by && <div style={{ fontSize: 11, color: '#555', marginTop: 3 }}>— {h.performed_by}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto', color: '#eee' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 },
  subtitle: { fontSize: 13, color: '#666', margin: '4px 0 0' },
  metricsRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
  metricCard: { background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 8, padding: '12px 18px', minWidth: 100, textAlign: 'center' },
  metricValue: { fontSize: 22, fontWeight: 700 },
  metricLabel: { fontSize: 11, color: '#666', marginTop: 2, textTransform: 'uppercase' },
  filtersRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: { flex: 1, minWidth: 200, background: '#111', border: '1px solid #333', borderRadius: 6, color: '#eee', padding: '8px 12px', fontSize: 13 },
  select: { background: '#111', border: '1px solid #333', borderRadius: 6, color: '#eee', padding: '8px 12px', fontSize: 13 },
  tableWrap: { background: '#111', border: '1px solid #222', borderRadius: 8, overflow: 'hidden' },
  tableHeader: { display: 'grid', gridTemplateColumns: '110px 110px 1fr 1fr 130px 80px 100px 90px', gap: 8, padding: '10px 14px', background: '#1a1a1a', fontSize: 11, color: '#666', textTransform: 'uppercase' },
  tableRow: { display: 'grid', gridTemplateColumns: '110px 110px 1fr 1fr 130px 80px 100px 90px', gap: 8, padding: '12px 14px', borderTop: '1px solid #1a1a1a', cursor: 'pointer', fontSize: 13, alignItems: 'center' },
  woNum: { fontWeight: 700, color: '#4ab0f0', fontFamily: 'monospace' },
  typeBadge: { display: 'inline-block', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 },
  statusBadge: { display: 'inline-block', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  empty: { padding: 40, textAlign: 'center', color: '#555', fontSize: 14 },
  btnPrimary: { background: '#4caf50', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { background: '#222', color: '#aaa', border: '1px solid #333', borderRadius: 6, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  backBtn: { background: '#1a1a1a', color: '#aaa', border: '1px solid #333', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #222' },
  tab: { background: 'none', border: 'none', color: '#666', padding: '8px 16px', fontSize: 13, cursor: 'pointer', borderBottom: '2px solid transparent' },
  tabActive: { color: '#4caf50', borderBottom: '2px solid #4caf50' },
  card: { background: '#111', border: '1px solid #222', borderRadius: 8, padding: 20, marginBottom: 16 },
  cardTitle: { fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 },
  fieldRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #1a1a1a' },
  fieldLabel: { fontSize: 12, color: '#666', minWidth: 100 },
  fieldValue: { fontSize: 13, color: '#ddd', cursor: 'pointer', textAlign: 'right' },
  inlineInput: { background: '#0a0a0a', border: '1px solid #4caf50', borderRadius: 4, color: '#eee', padding: '4px 8px', fontSize: 13 },
  btnSave: { background: '#4caf50', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
  btnCancel: { background: '#333', color: '#aaa', border: 'none', borderRadius: 4, padding: '4px 8px', fontSize: 12, cursor: 'pointer' },
  formBox: { background: '#111', border: '1px solid #222', borderRadius: 10, padding: 28, marginBottom: 16, maxWidth: 760 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px 20px' },
  formField: { display: 'flex', flexDirection: 'column', gap: 5 },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 },
  label: { fontSize: 11, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5, display: 'block' },
  input: { background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 6, color: '#eee', padding: '9px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  typeBtn: { background: '#1a1a1a', border: '1px solid #333', color: '#777', borderRadius: 8, padding: '10px 24px', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  typeBtnActive: { background: '#1a2a1a', border: '1px solid #4caf50', color: '#4caf50' },
  error: { marginTop: 16, padding: '10px 14px', background: '#2a1a1a', color: '#e57373', borderRadius: 6, fontSize: 13 },
};